import React, { useEffect, useState } from "react";
import { Loader2, Coins } from "lucide-react";
import { LeadsTable, OrdersTable, eur } from "./QualityTables";

const API = process.env.REACT_APP_BACKEND_URL;
const PERIODS = [["today", "Aujourd'hui"], ["7d", "7 jours"], ["30d", "30 jours"], ["month", "Ce mois"], ["all", "Tout"]];

const Card = ({ label, value, sub, tone = "text-white", testid }) => (
  <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
    <p className={`text-2xl font-extrabold mt-1 ${tone}`}>{value}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
  </div>
);

const Cards = ({ c }) => {
  const other = Object.entries(c.revenue_other || {}).map(([k, v]) => `+ ${v} ${k}`).join(" ");
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="quality-cards">
      <Card testid="q-card-validated" label="Bons leads" value={c.validated} tone="text-emerald-400" sub={`sur ${c.leads} leads`} />
      <Card testid="q-card-unqualified" label="Pas qualifiés" value={c.unqualified} tone="text-rose-400" />
      <Card testid="q-card-pending" label="En attente" value={c.pending} tone="text-amber-300" sub={c.verdict_na ? `${c.verdict_na} sans verdict (non disponible)` : null} />
      <Card testid="q-card-ga-leads" label="Leads envoyés Google Ads" value={c.ga_lead_sent} sub="Lead_Commande" />
      <Card testid="q-card-orders" label="Commandes" value={c.orders} />
      <Card testid="q-card-revenue" label="Chiffre d'affaires" value={eur(c.revenue_eur)} sub={other || null} />
      <Card testid="q-card-ga-orders" label="Commandes envoyées Google Ads" value={c.ga_order_sent} sub="Client_Commande" />
      <Card testid="q-card-ga-via" label="Envoyées par clic / appel" value={`${c.ga_order_click} / ${c.ga_order_call}`} />
    </div>
  );
};

const Commission = ({ m }) => {
  const month = new Date(`${m.month}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  return (
    <section className="mb-6 rounded-2xl border border-amber-400/20 bg-gradient-to-br from-amber-500/10 to-orange-500/5 p-4 sm:p-5 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="quality-commission">
      <div className="flex items-center gap-3">
        <Coins className="w-6 h-6 text-amber-300" />
        <div>
          <p className="text-[11px] uppercase tracking-wider text-amber-300 font-bold">Commissions estimées · {month}</p>
          <p className="text-3xl font-extrabold text-white" data-testid="quality-commission-total">{eur(m.total)}</p>
        </div>
      </div>
      <p className="text-sm text-slate-300">{m.audit} Audit × {m.rates.audit} € · {m.peb} PEB × {m.rates.peb} €</p>
      {m.unknown > 0 && <p className="text-xs text-slate-500">{m.unknown} commande(s) sans lead lié (type inconnu, non comptées)</p>}
    </section>
  );
};

const PerSite = ({ rows }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60 mb-6" data-testid="quality-by-site">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
          {["Site", "Leads", "Bons", "Pas qualifiés", "En attente", "Envoyés GAds", "Commandes", "CA", "Cmd GAds clic / appel"].map((h) => <th key={h} className="px-3 py-2.5 whitespace-nowrap">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.site_id} className="border-b border-slate-800/60 last:border-0">
            <td className="px-3 py-2.5 text-white whitespace-nowrap">{r.site_host}</td>
            <td className="px-3 py-2.5">{r.leads}</td>
            <td className="px-3 py-2.5 text-emerald-400">{r.validated}</td>
            <td className="px-3 py-2.5 text-rose-400">{r.unqualified}</td>
            <td className="px-3 py-2.5 text-amber-300">{r.pending}</td>
            <td className="px-3 py-2.5">{r.ga_lead_sent}</td>
            <td className="px-3 py-2.5">{r.orders}</td>
            <td className="px-3 py-2.5 whitespace-nowrap">{eur(r.revenue_eur)}</td>
            <td className="px-3 py-2.5">{r.ga_order_click} / {r.ga_order_call}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Pill = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold border transition-colors ${active ? "bg-amber-400 text-slate-900 border-amber-400" : "bg-slate-900/60 text-slate-300 border-slate-800 hover:border-slate-600"}`}>
    {children}
  </button>
);

const H2 = ({ children }) => (
  <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300 mb-3 flex items-center gap-2"><span className="w-1 h-5 bg-amber-400 rounded-full" />{children}</h2>
);

export const QualityTab = ({ pin, refreshKey }) => {
  const [period, setPeriod] = useState("30d");
  const [site, setSite] = useState("all");
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const qs = new URLSearchParams({ pin, period, site, include_test: includeTest });
    setError("");
    fetch(`${API}/api/dash/quality?${qs}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setData)
      .catch(() => setError("Impossible de charger les données qualité."));
  }, [pin, period, site, includeTest, refreshKey]);

  return (
    <div data-testid="quality-tab">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {PERIODS.map(([k, l]) => <Pill key={k} active={period === k} onClick={() => setPeriod(k)} testid={`quality-period-${k}`}>{l}</Pill>)}
        <select value={site} onChange={(e) => setSite(e.target.value)} data-testid="quality-site-select"
          className="ml-auto bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-1.5 text-sm text-slate-200">
          <option value="all">Tous les sites</option>
          {(data?.sites || []).map((s) => <option key={s.site_id} value={s.site_id}>{s.site_host}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
          <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} data-testid="quality-include-test" />
          Inclure les tests
        </label>
      </div>
      {error && <p className="text-rose-300 text-sm mb-4" data-testid="quality-error">{error}</p>}
      {!data && !error && <div className="flex justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {data && (
        <>
          <Commission m={data.commission} />
          <Cards c={data.cards} />
          {data.by_site.length > 0 && (<><H2>Par site</H2><PerSite rows={data.by_site} /></>)}
          <H2>Leads ({data.leads_count}{data.leads_count > data.leads.length ? `, 500 affichés` : ""})</H2>
          <div className="mb-6"><LeadsTable leads={data.leads} /></div>
          <H2>Commandes ({data.orders_count}{data.orders_count > data.orders.length ? ", 500 affichées" : ""})</H2>
          <OrdersTable orders={data.orders} />
        </>
      )}
    </div>
  );
};
